import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import styled from 'styled-components';
import _get from 'lodash/get';

import { IconInformation, Typography } from '@klarna/bubble-ui';
import { translator } from 'mage-i18n';
import { Column, GridContainer, Row, VerticalRhythm, withResponsive } from 'mage-components';
import { Timeline } from '@klarna/moet-ui-timeline';
import { FlowContext, Alert } from 'moet-core';
import { isRunningContextCypress } from 'moet-test-utils/cypress/utils';

import {
  BASIC_STEP_PATHS,
  BUSINESS_STEP_PATHS,
  STAKEHOLDER_STEP_PATHS,
  BANK_ACCOUNT_STEP_PATHS,
} from '@/constants/paths';
import MidSelector from '@/containers/MidSelector/MidSelector';
import { isKustomPartner } from '@/selectors/data';

/* eslint-disable no-restricted-imports */
import step1 from '../../static/BrandAssetLibrary_2024_ConsumerBenefit_Slide_4x5.jpg';
import step2 from '../../static/BrandAssetLibrary_2024_ConsumerBenefit_ProductTower_4x5.jpg';
import step3 from '../../static/StockImagery2024_Device_Backside_4x5_V4.jpg';
import kustomSideImage from '../../static/kustom-side-image.png';
/* eslint-disable no-restricted-imports */

import { RouteTransitionWrapper } from './RouteTransitionWrapper';
import { ErrorAlert } from './ErrorAlert';
import SplitImageFooter from './SplitimageFooter';
import SplitImageTopBar from './SplitImageTopBar';
import { getWidgetTypeFromWidgetCookie, IS_BPC_APP } from '@/selectors/widget';
import { WIDGET_TYPE } from 'moet-core/src/constants';
import { getDecodedWidgetToken } from '@/utils/widget';
import greyBlock from '../../static/grey-block-image.png';
import { IS_MERCHANT_VERIFICATION_REVIEW_INFO } from '@/constants';

const tPrefix = 'products-fe.components.split_image_container';

const SplitImage = styled.div`
  background: ${props => `url(${props.url}) center no-repeat`};
  background-color: #eb6f93;
  background-size: cover;
  height: 100vh;
  width: 50vw;
  z-index: 1;
  position: fixed;
`;

const ContentBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  margin: 0 auto;
  max-width: ${props => (props.width > 6 ? '540px' : 'unset')};
`;

const THEME_TO_IMAGE = { step1, step2, step3 };

const STEPS = {
  basics: 'timeline.basics',
  business: 'timeline.business',
  stakeholders: 'timeline.stakeholders',
  bankAccounts: 'timeline.bank_accounts',
};

const STEP_TO_PATH = {
  [STEPS.basics]: Object.values(BASIC_STEP_PATHS),
  [STEPS.business]: Object.values(BUSINESS_STEP_PATHS),
  [STEPS.stakeholders]: Object.values(STAKEHOLDER_STEP_PATHS),
  [STEPS.bankAccounts]: Object.values(BANK_ACCOUNT_STEP_PATHS),
};

const getCurrentStep = () => {
  const matches = window.location.pathname.match(/\/products\/((signup\/)?.+)/);

  if (!matches || matches.length < 2) {
    return null;
  }

  return matches[1];
};

const getTimelineStepTKey = () => {
  const currentStep = getCurrentStep();

  return Object.keys(STEP_TO_PATH).find(key => STEP_TO_PATH[key].includes(currentStep));
};

export const SplitImageContainerComponent = ({
  children = null,
  contentWidth = 6,
  disableAnimations,
  error = null,
  imageSource,
  info,
  isMobile,
  subtitle,
  t,
  title,
  showRecaptchaFooter,
  widgetType,
  isKustomPartner,
}) => {
  const { navigation = {} } = useContext(FlowContext);
  const currentTheme = _get(navigation, 'theme.to');

  const timelineStepTKey = getTimelineStepTKey();
  const shouldShowTimeline = ![
    WIDGET_TYPE.DOCUMENT_UPLOAD,
    WIDGET_TYPE.MANAGED_KYC,
    WIDGET_TYPE.NATIVE_KYC,
  ].includes(widgetType);
  const decodedWidgetToken = getDecodedWidgetToken();

  const finalImageSource = isKustomPartner
    ? kustomSideImage
    : imageSource || THEME_TO_IMAGE[currentTheme] || step1;

  return (
    <GridContainer
      extraStyles={{
        overflow: 'unset',
        maxWidth: 'unset',
        display: 'flex',
        minHeight: '100vh',
        flexDirection: 'column',
        backgroundColor: 'rgb(255,255,255)',
      }}
    >
      {decodedWidgetToken?.hideTopBar ? null : <SplitImageTopBar />}
      <Row spacing={{ mobile: 0 }} extraStyles={{ flex: 1 }}>
        <Column
          columns={{ mobile: 12, desktop: contentWidth > 6 ? 12 : 6 }}
          justify={{ mobile: 'space-between' }}
        >
          <Row justify="center" spacing={{ mobile: 0 }}>
            <Column columns={{ mobile: 10, tablet: 9 }}>
              {shouldShowTimeline && timelineStepTKey && (
                <>
                  <VerticalRhythm spacing={{ mobile: 10, tablet: 14 }} />
                  <Timeline
                    steps={Object.values(STEPS).map(value => t(`${tPrefix}.${value}`))}
                    currentStep={t(`${tPrefix}.${timelineStepTKey}`)}
                  />
                </>
              )}
              <RouteTransitionWrapper
                name={navigation.key}
                direction={navigation.direction}
                disabled={isRunningContextCypress(window) || disableAnimations}
              >
                <ContentBlock>
                  <VerticalRhythm spacing={{ mobile: 5, tablet: 9 }} />
                  {!IS_BPC_APP && <MidSelector />}
                  <VerticalRhythm spacing={{ mobile: 5, tablet: 9 }} />
                  {isMobile ? (
                    <Typography
                      textToken="text-style/headings/titles/bold/primary"
                      accessibilityPreset="heading 1"
                    >
                      {title}
                    </Typography>
                  ) : (
                    <Typography
                      textToken="text-style/headings/titles/bold/huge"
                      accessibilityPreset="heading 1"
                    >
                      {title}
                    </Typography>
                  )}
                  {subtitle && (
                    <>
                      <VerticalRhythm spacing={{ mobile: 6 }} />
                      {isMobile ? (
                        <Typography textToken="text-style/text/super-paragraphs/body/regular">
                          {subtitle}
                        </Typography>
                      ) : (
                        <Typography textToken="text-style/text/super-paragraphs/tall/regular">
                          {subtitle}
                        </Typography>
                      )}
                    </>
                  )}
                  {error && (
                    <>
                      <VerticalRhythm spacing={{ mobile: 6 }} />
                      <ErrorAlert error={error} />
                    </>
                  )}
                  {info && (
                    <>
                      <VerticalRhythm spacing={{ mobile: 6 }} />
                      <Alert content={info} icon={IconInformation} />
                    </>
                  )}
                  <VerticalRhythm spacing={{ mobile: 6, tablet: 10 }} />
                  {children}
                  <VerticalRhythm spacing={{ mobile: 10, tablet: 12 }} />
                </ContentBlock>
              </RouteTransitionWrapper>
            </Column>
          </Row>
          <Row spacing={{ mobile: 0 }} justify="center">
            <Column columns={{ mobile: 10, tablet: 11 }}>
              <SplitImageFooter showRecaptchaFooter={showRecaptchaFooter} />
            </Column>
          </Row>
        </Column>
        <Column
          columns={{ desktop: 6 }}
          display={{
            mobile: 'none',
            tablet: 'none',
            ...(contentWidth > 6 ? { desktop: 'none' } : {}),
          }}
        >
          {widgetType === WIDGET_TYPE.MERCHANT_VERIFICATION ||
          IS_MERCHANT_VERIFICATION_REVIEW_INFO ? (
            <SplitImage url={greyBlock} />
          ) : (
            <SplitImage url={finalImageSource} />
          )}
        </Column>
      </Row>
    </GridContainer>
  );
};

SplitImageContainerComponent.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  children: PropTypes.node,
  contentWidth: PropTypes.number,
  disableAnimations: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  imageSource: PropTypes.string,
  info: PropTypes.string,
  isKustomPartner: PropTypes.bool,
  showRecaptchaFooter: PropTypes.bool,
  subtitle: PropTypes.node,
  title: PropTypes.string,
  widgetType: PropTypes.string,
};

export const SplitImageContainer = compose(
  connect(state => ({
    t: translator(state),
    widgetType: getWidgetTypeFromWidgetCookie(state),
    isKustomPartner: isKustomPartner(state),
  })),
  withResponsive,
)(SplitImageContainerComponent);
